// eslint-disable-next-line @typescript-eslint/ban-ts-comment -- legacy class is intentionally unchecked
// @ts-nocheck
import React from 'react';
import PropTypes from 'prop-types';
import Form from 'react-bootstrap/Form';
import { debounce, isNil } from 'lodash';
import { TYPE_TEXT } from '../../../../constants';
import { GetValidationPattern, isEqualLowerCase } from '../../../../utils/ValidationUtil';
import { formatClassName } from '../../../../utils';
import { i18n } from '../../../../i18n/translate';

// FormField/FloatingLabelFormField bookkeeping must not be forwarded to a native input.
const NON_DOM_PROPS = [
  'activeLanguage',
  'caption',
  'data',
  'dataObjectAction',
  'dataObjectAttribute',
  'dataObjectId',
  'dataObjectType',
  'dataValue',
  'disableSubmit',
  'errorColor',
  'errorMessage',
  'fieldName', // re-emitted lowercase in render(): dashifi's RegistrationBase queries input[fieldname=...]
  'hideControlLabelAria',
  'hintLink',
  'hintText',
  'inlineStacked',
  'inputClassName',
  'ignorePattern',
  'isFloatOutside',
  'labelClassName',
  'languages',
  'legendHeadingLevel',
  'multilingual',
  'onValueChange',
  'populatedOverride',
  'runValidation',
  'scrollContainerId',
  'searchField',
  'setError',
  'srHelpText',
  'tooltip',
  'tooltipPosition',
  'validationType',
  'valueChangeDelay',
  'valueOverride',
  'wrapperClassName',
];

export class FormControl extends React.Component {
  static propTypes = {
    activeLanguage: PropTypes.any,
    caption: PropTypes.string,
    className: PropTypes.string,
    as: PropTypes.string,
    dataValue: PropTypes.any,
    disableSubmit: PropTypes.bool,
    errorMessage: PropTypes.string,
    languages: PropTypes.any,
    max: PropTypes.string,
    min: PropTypes.string,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onValueChange: PropTypes.func,
    pattern: PropTypes.string,
    required: PropTypes.bool,
    runValidation: PropTypes.any,
    scrollContainerId: PropTypes.string,
    searchField: PropTypes.any,
    setError: PropTypes.func,
    type: PropTypes.string,
    valueChangeDelay: PropTypes.number,
    valueOverride: PropTypes.bool,
    ignorePattern: PropTypes.bool,
  };

  static defaultProps = {
    valueChangeDelay: 1000,
  };

  constructor(props) {
    super(props);
    this.isValid = this.isValid.bind(this);
    this.checkError = this.checkError.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onFormChange = this.onFormChange.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.debouncedValueChange = debounce(this.debouncedValueChange.bind(this), this.props.valueChangeDelay, {
      leading: false,
      trailing: true,
    });
    this.onBlur = this.onBlur.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.state = { value: !isNil(props.dataValue) ? props.dataValue : '' };
    if (!props.ignorePattern) {
      this.pattern = GetValidationPattern(props.type, props.pattern);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.valueOverride && this.props.dataValue !== prevProps.dataValue) {
      this.onChange(this.props.dataValue, true);
    }
    if (!this.props.ignorePattern && prevProps.pattern !== this.props.pattern) {
      this.pattern = GetValidationPattern(this.props.type, this.props.pattern);
    }
  }

  onFormChange(e) {
    this.onChange(e.currentTarget.value);
  }

  onChange(value, skipValueChange) {
    if (typeof this.props.onChange === 'function') {
      this.props.onChange(value);
    }
    if (!skipValueChange) {
      this.debouncedValueChange(value);
    }
    this.setState({ value });
  }

  isValid() {
    if (this.props.isValid && typeof this.props.isValid === 'function') {
      if (!this.props.isValid()) {
        this.input.setCustomValidity(i18n.t('Please enter a valid value'));
      } else {
        this.input.setCustomValidity('');
      }
      return this.props.isValid();
    }
    return this.input.validity.valid;
  }

  onBlur(e) {
    if (typeof this.props.onBlur === 'function') {
      this.props.onBlur(this.state.value);
    }
    this.checkError();

    if (!e?.currentTarget?.value) return;

    if (e.currentTarget.value !== this.props.dataValue) {
      if (typeof this.props.onValueChange === 'function') {
        this.props.onValueChange({ [this.props.name]: e.currentTarget.value });
      }
      this.debouncedValueChange.cancel();
    }
  }

  debouncedValueChange(value) {
    if (typeof this.props.onValueChange === 'function') {
      this.props.onValueChange({ [this.props.name]: value });
    }
  }

  onFocus() {
    if (typeof this.props.onFocus === 'function') {
      this.props.onFocus();
    }
  }

  checkError() {
    if (this.input) {
      let message = null;
      const isValid = this.isValid();
      const fieldLabel = this.props.caption || this.props.name;
      const isEmailField = this.props.type?.toLowerCase() === 'email';
      const isEmailFormatError =
        isEmailField && (this.input.validity.typeMismatch || this.input.validity.patternMismatch);

      if (this.input.validity.valueMissing) {
        message = fieldLabel
          ? i18n.t('%s is required', { postProcess: 'sprintf', sprintf: [fieldLabel] })
          : i18n.t('This field is required');
      } else if (isEmailFormatError) {
        message = fieldLabel
          ? i18n.t('Enter a valid email address for %s in the format name@example.com.', {
              postProcess: 'sprintf',
              sprintf: [fieldLabel],
            })
          : i18n.t('Enter a valid email address in the format name@example.com.');
      } else if (!this.input.validity.valid) {
        // Use a field-specific message instead of the raw browser validationMessage
        message = fieldLabel
          ? i18n.t('The value entered for %s is invalid', { postProcess: 'sprintf', sprintf: [fieldLabel] })
          : i18n.t('The value you entered is invalid');
      }

      if (this.input.required && isEqualLowerCase(this.props.type, TYPE_TEXT) && this.state.value.trim().length === 0) {
        const requiredMessage = fieldLabel
          ? i18n.t('%s is required', { postProcess: 'sprintf', sprintf: [fieldLabel] })
          : i18n.t('This field is required');
        this.props.setError(true, requiredMessage);
        return;
      }

      if (!message && isEmailField) {
        const pattern = new RegExp(GetValidationPattern(this.props.type));
        if (!pattern.test(this.state.value)) {
          message = fieldLabel
            ? i18n.t('Enter a valid email address for %s in the format name@example.com.', {
                postProcess: 'sprintf',
                sprintf: [fieldLabel],
              })
            : i18n.t('Enter a valid email address in the format name@example.com.');
        }
      }

      if (typeof this.props.setError === 'function') {
        this.props.setError(!isValid, message);
      }
    }
  }

  handleKeyPress(e) {
    if (this.props.disableSubmit && e.key === 'Enter') {
      e.preventDefault();
    }
  }

  render() {
    const { as, className } = this.props;
    const props = { ...this.props };
    NON_DOM_PROPS.concat(['as', 'className']).forEach(prop => delete props[prop]);

    const newClassName = formatClassName([className, { 'p-r-10': as === 'textarea' }]);

    return (
      <Form.Control
        {...props}
        as={as}
        className={newClassName}
        data-field="input-field"
        fieldname={this.props.fieldName}
        onBlur={this.onBlur}
        onChange={this.onFormChange}
        onFocus={this.onFocus}
        onKeyPress={this.handleKeyPress}
        pattern={this.pattern}
        ref={input => (this.input = input)}
        value={this.state.value}
      />
    );
  }
}
